// Access token lives in a module-level singleton — NOT in localStorage / sessionStorage
// (XSS rules). Cleared on page reload, recovered via POST /auth/refresh on app start.
// Refresh token is the browser-managed httpOnly `cb_refresh` cookie.

let _token: string | null = null;

// Persisted "this browser has had a real session" hint. The access token lives
// in memory only (wiped on reload), so on an anonymous first visit there is no
// way to know whether a POST /auth/refresh is worth attempting — and a blind
// attempt 401s in the console for every cold visitor. This flag (set on login /
// refresh-success, cleared on sign-out / refresh-failure) lets the api client
// skip the refresh probe for visitors who were never signed in, while still
// recovering the session on F5 for users who were. Non-httpOnly by necessity;
// it carries no secret, just a boolean breadcrumb.
const SESSION_HINT_KEY = 'dgs_session_hint';

function hintStore(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

export function hasSessionHint(): boolean {
  return hintStore()?.getItem(SESSION_HINT_KEY) === '1';
}

export function getAccessToken(): string | null {
  return _token;
}

export function setAccessToken(token: string | null): void {
  _token = token;
  const store = hintStore();
  if (!store) return;
  if (token) store.setItem(SESSION_HINT_KEY, '1');
  else store.removeItem(SESSION_HINT_KEY);
}

export function clearAccessToken(): void {
  _token = null;
  hintStore()?.removeItem(SESSION_HINT_KEY);
}

// --- Impersonation ("Login as") --------------------------------------------
//
// While a superuser impersonates a target user, the *active* access token
// (`_token`) is the genuine impersonation token returned by the backend, so
// every request runs as that user. The admin's own token is stashed in a
// separate module-level slot so "Exit" can restore it without a network call.
//
// Both slots are memory-only (same XSS rule as the access token). On a hard
// reload BOTH are lost, and the page-load /auth/refresh flow mints a fresh
// token from the httpOnly `cb_refresh` cookie — which still belongs to the
// ADMIN (the swap never touched the refresh cookie). So F5 mid-impersonation
// cleanly drops back to the admin session, which is the safe failure mode.
//
// The impersonation token itself carries an `act` (actor) claim identifying
// the admin behind it; we decode it client-side (no signature check) purely
// to drive UI — the banner + api layer. The presence of a stashed admin token
// is the source of truth for "impersonation active".

let _impersonationAdminToken: string | null = null;

export function isImpersonating(): boolean {
  return _impersonationAdminToken !== null;
}

export function beginImpersonation(impersonationToken: string): void {
  // Stash the CURRENT (admin) token, then swap the active token. Guard against
  // double-enter clobbering the real admin token with another impersonation
  // token: only stash if we aren't already impersonating.
  if (_impersonationAdminToken === null) {
    _impersonationAdminToken = _token;
  }
  _token = impersonationToken;
}

// Restore the admin token and clear the stash. Returns the restored admin
// token (or null if there was none / it had been lost to a reload).
export function endImpersonation(): string | null {
  const adminToken = _impersonationAdminToken;
  _impersonationAdminToken = null;
  _token = adminToken;
  return adminToken;
}

interface JwtPayload {
  sub?: string;
  email?: string;
  exp?: number;
  // Actor claim set by the backend on impersonation tokens — the admin id/email
  // behind the impersonation. Shape mirrors RFC 8693 `act`.
  act?: { sub?: string; email?: string } | string;
  [k: string]: unknown;
}

// Decode a JWT payload WITHOUT verifying the signature. Safe for read-only UI
// decisions only — never for authorization. Returns null on any malformed input.
export function decodeJwtPayload(token: string | null): JwtPayload | null {
  if (!token) return null;
  const parts = token.split('.');
  if (parts.length !== 3) return null;
  try {
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
    const json = atob(padded);
    return JSON.parse(json) as JwtPayload;
  } catch {
    return null;
  }
}

// Types mirror the backend Pydantic envelopes EXACTLY — backend returns flat
// shapes, no `{data: ...}` wrapping. See backend/app/schemas/auth.py.

export interface User {
  id: string;
  email: string;
  display_name?: string | null;
  email_verified: boolean;
  created_at?: string;
  is_superuser?: boolean;
}

export interface Quota {
  used_this_period: number;
  period_cap: number | null;
  lifetime_used: number;
  lifetime_cap: number | null;
  period_start: string | null;
  period_end: string | null;
}

export interface Org {
  id: string;
  name: string;
  plan: 'free' | 'pro' | 'team' | 'enterprise';
  is_personal: boolean;
}

export interface Membership {
  id: string;
  org_id: string;
  role: 'owner' | 'admin' | 'member' | 'viewer';
}

export interface Subscription {
  id: string;
  plan: 'free' | 'pro' | 'team' | 'enterprise';
  status: 'trialing' | 'active' | 'past_due' | 'paused' | 'canceled' | 'expired';
  cancel_at_period_end: boolean;
  current_period_end: string;
  canceled_at: string | null;
}

// Per-org S3 storage usage snapshot, surfaced in /me. Mirrors
// backend StorageQuotaOut.
export interface StorageQuota {
  bytes_used: number;
  bytes_limit: number | null; // null = unlimited (Enterprise)
  bytes_remaining: number | null; // null when unlimited
}

// GET /v1/me returns this shape verbatim (no wrapper).
export interface MePayload {
  user: User;
  org: Org;
  memberships: Membership[];
  quota: Quota;
  storage: StorageQuota;
  // Null when org is on free plan with no active Paddle subscription.
  // Populated so Billing UI can render "Cancels on …" after a reload.
  subscription: Subscription | null;
}
